import { GPSData } from "PosterEditor/model/poster-project";

// Utilities to format GPSData into user-facing strings
// Assumes GPSData is defined elsewhere in the codebase.

export type UnitSystem = "metric" | "imperial";

export type GPSDataFormatted = {
  // e.g. "6.24 km" or "3.88 mi" or "742 m" / "2,100 ft"
  distance: string;
  // e.g. "16 Aug 2024" (no time)
  startDate?: string;
  // Only present if end date is a different calendar date than start (in the chosen timeZone)
  endDate?: string;
  // e.g. "1h 23m 06s", "3m 12s", "2d 4h 11m"
  elapsed?: string;
};

/**
 * Formats GPSData into human-readable strings.
 * - Distance: metric (m / km) or imperial (ft / mi) based on `unitSystem`.
 * - Dates: outputs calendar dates only (no time). If start & end are same calendar day, omits endDate.
 * - Elapsed: days/hours/minutes/seconds, omitting leading zero units (no 0d prefix, etc.).
 */
export function formatGPSData(
  data: GPSData,
  unitSystem: UnitSystem,
  opts?: { locale?: string; timeZone?: string }
): GPSDataFormatted {
  const { distanceMeters, start, end } = data;
  const locale = opts?.locale; // default browser/host locale if undefined
  const timeZone = opts?.timeZone; // default system tz if undefined

  // ---- Distance ----
  const distance = formatDistance(distanceMeters, unitSystem, locale);

  // ---- Dates (no time) ----
  const { startDate, endDate } = formatDates(start, end, { locale, timeZone });

  // ---- Elapsed ----
  const elapsed = formatElapsed(start, end);

  return { distance, startDate, endDate, elapsed };
}

function formatDistance(
  meters: number,
  unitSystem: UnitSystem,
  locale?: string
): string {
  if (!isFinite(meters) || meters < 0) meters = 0;

  if (unitSystem === "imperial") {
    const feet = meters * 3.28084;
    const miles = meters / 1609.344;
    if (miles < 1) {
      // Show feet under 1 mile
      const wholeFeet = Math.round(feet);
      return `${new Intl.NumberFormat(locale).format(wholeFeet)} ft`;
    }
    const milesFmt = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    });
    return `${milesFmt.format(miles)} mi`;
  }

  // metric
  if (meters < 1000) {
    const wholeMeters = Math.round(meters);
    return `${new Intl.NumberFormat(locale).format(wholeMeters)} m`;
  }
  const km = meters / 1000;
  const kmFmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  return `${kmFmt.format(km)} km`;
}

function formatDates(
  start?: Date | string,
  end?: Date | string,
  opts?: { locale?: string; timeZone?: string }
): { startDate?: string; endDate?: string } {
  if (!start) return {};
  const { locale, timeZone } = opts || {};

  const dateFmt = new Intl.DateTimeFormat(locale, {
    timeZone,
    dateStyle: "medium",
  });

  const startDateObj = typeof start === "string" ? new Date(start) : start;
  const startDate = dateFmt.format(startDateObj);

  if (!end) return { startDate };

  const endDateObj = typeof end === "string" ? new Date(end) : end;

  // Only include endDate if it's a different calendar day than start
  const same = isSameCalendarDay(startDateObj, endDateObj, {
    locale,
    timeZone,
  });
  if (same) return { startDate };

  const endDate = dateFmt.format(endDateObj);
  return { startDate, endDate };
}

function isSameCalendarDay(
  a: Date | string,
  b: Date | string,
  opts?: { locale?: string; timeZone?: string }
): boolean {
  const { locale, timeZone } = opts || {};

  const dateA = typeof a === "string" ? new Date(a) : a;
  const dateB = typeof b === "string" ? new Date(b) : b;

  const fmt = new Intl.DateTimeFormat(locale, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const key = (d: Date) => {
    const parts = fmt.formatToParts(d);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const da = parts.find((p) => p.type === "day")?.value;
    return `${y}-${m}-${da}`;
  };
  return key(dateA) === key(dateB);
}

function formatElapsed(
  start?: Date | string,
  end?: Date | string
): string | undefined {
  if (!start || !end) return undefined;

  const startDate = typeof start === "string" ? new Date(start) : start;
  const endDate = typeof end === "string" ? new Date(end) : end;

  const ms = endDate.getTime() - startDate.getTime();
  if (!isFinite(ms) || ms < 0) return undefined;

  let remaining = Math.floor(ms / 1000); // seconds
  const days = Math.floor(remaining / 86400);
  remaining -= days * 86400;
  const hours = Math.floor(remaining / 3600);
  remaining -= hours * 3600;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining - minutes * 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  // Always include seconds (even if zero) when everything else is zero
  if (parts.length === 0 || seconds > 0)
    parts.push(`${seconds.toString().padStart(2, "0")}s`);

  return parts.join(" ");
}
